import React, { useState, useRef, useEffect } from 'react';
import {
  X, Minus, Maximize2, Minimize2, GripHorizontal, FileText, Mic, Bot,
  Pause, Square, Circle, Eye, EyeOff, AlertCircle, ChevronDown
} from 'lucide-react';
import { useNotesWindow, NoteTemplate, StructuredNoteContent } from '../../contexts/NotesWindowContext';

/**
 * FLOATING NOTES WINDOW
 *
 * Phase 6X implementation - floating/draggable notes window with:
 * - Path selection (Structured vs AI-Assisted)
 * - Template selection for structured notes
 * - Voice transcription with field locking
 * - Full navigation while window is open
 */

// Size constraints
const MIN_WIDTH = 450;
const MIN_HEIGHT = 550;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 900;
const DEFAULT_WIDTH = 580;
// Dynamic default height: use available viewport height (minus margin for taskbar/padding)
const getDefaultHeight = () => Math.min(MAX_HEIGHT, window.innerHeight - 80);

const TEMPLATES: { id: NoteTemplate; name: string; description: string }[] = [
  { id: 'soap_session', name: 'SOAP Session Note', description: 'Structured clinical note (S/O/A/P sections)' },
  { id: 'quick_note', name: 'Quick Note', description: 'Freeform text note' }
];

const FIELD_LABELS: Record<keyof StructuredNoteContent, string> = {
  subjective: 'Subjective',
  objective: 'Objective',
  assessment: 'Assessment',
  plan: 'Plan'
};

export const FloatingNotesWindow: React.FC = () => {
  const {
    isOpen,
    isMinimized,
    isEditMode,
    editingNoteId,
    currentPath,
    selectedTemplate,
    clientId,
    clientName,
    initialTags,
    fieldContents,
    fieldVisibility,
    activeField,
    lockedTargetField,
    noteTitle,
    noteDate,
    unstructuredContent,
    isRecording,
    isPaused,
    recordingDuration,
    interimTranscript,
    closeWindow,
    toggleMinimized,
    selectPath,
    selectTemplate,
    setActiveField,
    updateFieldContent,
    toggleFieldVisibility,
    setNoteTitle,
    setNoteDate,
    setUnstructuredContent,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetWindow,
    convertToUnstructured,
    openWindow
  } = useNotesWindow();

  // Position and size state for floating mode - centered on screen
  const defaultHeight = getDefaultHeight();
  const [position, setPosition] = useState({
    x: Math.max(0, (window.innerWidth - DEFAULT_WIDTH) / 2),
    y: Math.max(0, (window.innerHeight - defaultHeight) / 2)
  });
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: defaultHeight });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const interimPreviewRef = useRef<HTMLDivElement>(null);

  // Convert to AI confirmation
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);

  // Unsaved changes confirmation
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  // Expanded field state (null = all normal, field name = that field expanded)
  const [expandedField, setExpandedField] = useState<keyof StructuredNoteContent | null>(null);

  // Tags for the note - chip/pill UI
  const [noteTags, setNoteTags] = useState<string[]>([]);
  const [noteTagsInput, setNoteTagsInput] = useState<string>('');

  // Initialize tags from context when opening in edit mode
  useEffect(() => {
    if (isEditMode && initialTags.length > 0) {
      setNoteTags(initialTags);
    }
  }, [isEditMode, initialTags]);

  // Option to save raw transcript as attachment (AI-assisted flow)
  const [saveTranscript, setSaveTranscript] = useState(true);

  // Handle tag input keydown (comma, enter to add; backspace to delete)
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = noteTagsInput.trim();

    // Comma or Enter: commit tag
    if ((e.key === ',' || e.key === 'Enter') && value) {
      e.preventDefault();
      // Don't add duplicates
      if (!noteTags.includes(value.toLowerCase())) {
        setNoteTags([...noteTags, value.toLowerCase()]);
      }
      setNoteTagsInput('');
    }

    // Backspace on empty input: delete last tag
    if (e.key === 'Backspace' && !noteTagsInput && noteTags.length > 0) {
      setNoteTags(noteTags.slice(0, -1));
    }
  };

  // Remove a specific tag
  const removeTag = (tagToRemove: string) => {
    setNoteTags(noteTags.filter(t => t !== tagToRemove));
  };

  // Post-processing wizard state (null = not in wizard, step name = current wizard screen)
  type WizardStep = 'options' | 'processing' | 'review' | null;
  const [wizardStep, setWizardStep] = useState<WizardStep>(null);

  // What type of note are we generating? (used by processing screen)
  type GenerationType = 'raw' | 'soap' | 'checkin' | null;
  const [generationType, setGenerationType] = useState<GenerationType>(null);

  // Generated/processed content (for review screen)
  const [processedContent, setProcessedContent] = useState<string>('');

  // Structured SOAP data from DEAN (separate from display string)
  const [processedSOAP, setProcessedSOAP] = useState<{
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  } | null>(null);

  // Processing error state
  const [processingError, setProcessingError] = useState<string | null>(null);

  // Processing progress state (for streaming)
  const [processingProgress, setProcessingProgress] = useState({
    tokenCount: 0,
    elapsedSeconds: 0,
    fakeProgress: 0 // 0-100 "for show" progress bar
  });
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Crash protection - recovery state
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [recoveryData, setRecoveryData] = useState<DraftData | null>(null);
  const DRAFT_STORAGE_KEY = 'floating-notes-draft';

  // Draft data shape for localStorage
  interface DraftData {
    clientId: string | null;
    clientName: string | null;
    currentPath: 'structured' | 'unstructured' | null;
    selectedTemplate: NoteTemplate | null;
    fieldContents: StructuredNoteContent;
    fieldVisibility: Record<string, boolean>;
    unstructuredContent: string;
    noteTitle: string;
    noteDate: string;
    noteTags: string[];
    noteTagsInput: string;
    wizardStep: WizardStep;
    generationType: GenerationType;
    processedContent: string;
    saveTranscript: boolean;
    savedAt: string;
  }

  // Check if speech recognition is available
  const speechAvailable = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Auto-scroll interim preview to bottom as new content arrives
  useEffect(() => {
    if (interimPreviewRef.current && interimTranscript) {
      interimPreviewRef.current.scrollTop = interimPreviewRef.current.scrollHeight;
    }
  }, [interimTranscript]);

  // Cleanup processing timer on unmount
  useEffect(() => {
    return () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
      }
    };
  }, []);

  // Check for recovery data on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved) as DraftData;
        // Only show recovery if there's actual content
        const hasContent = data.unstructuredContent?.trim() ||
          Object.values(data.fieldContents || {}).some(v => v?.trim());
        if (hasContent) {
          setRecoveryData(data);
          setShowRecoveryPrompt(true);
        } else {
          // Clean up empty drafts
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to load draft:', e);
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, []);

  // Auto-save draft to localStorage when content changes
  useEffect(() => {
    // Only save if window is open and has content
    if (!isOpen) return;
    if (!currentPath) return; // Don't save if still on path selection

    const hasContent = unstructuredContent?.trim() ||
      Object.values(fieldContents).some(v => v?.trim());
    if (!hasContent) return;

    const draft: DraftData = {
      clientId,
      clientName,
      currentPath,
      selectedTemplate,
      fieldContents,
      fieldVisibility,
      unstructuredContent,
      noteTitle,
      noteDate,
      noteTags,
      noteTagsInput,
      wizardStep,
      generationType,
      processedContent,
      saveTranscript,
      savedAt: new Date().toISOString()
    };

    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  }, [
    isOpen, currentPath, selectedTemplate, fieldContents, fieldVisibility,
    unstructuredContent, noteTitle, noteDate, noteTags, noteTagsInput, wizardStep, generationType,
    processedContent, saveTranscript, clientId, clientName
  ]);

  // Clear draft from localStorage
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  // Recover draft data
  const recoverDraft = () => {
    if (!recoveryData) return;

    // Copy the data before any state changes (closure safety)
    const data = { ...recoveryData };

    // Clear the recovery UI first
    setShowRecoveryPrompt(false);
    setRecoveryData(null);

    // Open window (this resets path/template to null)
    openWindow(data.clientId || '', data.clientName || 'Unknown Client');

    // First setTimeout: restore path and template after openWindow's state settles
    setTimeout(() => {
      if (data.currentPath) selectPath(data.currentPath);
      if (data.selectedTemplate) selectTemplate(data.selectedTemplate);

      // Second setTimeout: restore content AFTER selectTemplate's reset settles
      // (selectTemplate resets fieldContents to defaults)
      setTimeout(() => {
        // Restore field contents
        if (data.fieldContents) {
          const fields = ['subjective', 'objective', 'assessment', 'plan'] as const;
          for (const field of fields) {
            if (data.fieldContents[field]) {
              updateFieldContent(field, data.fieldContents[field]);
            }
          }
        }

        // Restore field visibility
        if (data.fieldVisibility) {
          const defaultVis: Record<string, boolean> = {
            subjective: false,
            objective: false,
            assessment: false,
            plan: true
          };
          const fields = ['subjective', 'objective', 'assessment', 'plan'] as const;
          for (const field of fields) {
            const desiredVis = data.fieldVisibility[field];
            if (typeof desiredVis === 'boolean' && desiredVis !== defaultVis[field]) {
              toggleFieldVisibility(field);
            }
          }
        }

        if (data.unstructuredContent) setUnstructuredContent(data.unstructuredContent);
        if (data.noteTitle) setNoteTitle(data.noteTitle);
        if (data.noteDate) setNoteDate(data.noteDate);
        if (data.noteTags) setNoteTags(data.noteTags);
        if (data.noteTagsInput) setNoteTagsInput(data.noteTagsInput);
        if (data.wizardStep) setWizardStep(data.wizardStep);
        if (data.generationType) setGenerationType(data.generationType);
        if (data.processedContent) setProcessedContent(data.processedContent);
        if (typeof data.saveTranscript === 'boolean') setSaveTranscript(data.saveTranscript);
      }, 0);
    }, 0);
  };

  // Dismiss recovery and clear draft
  const dismissRecovery = () => {
    clearDraft();
    setShowRecoveryPrompt(false);
    setRecoveryData(null);
  };

  // Format recording duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      // Use smaller bounds when minimized (bar is ~50px tall, ~300px wide)
      const effectiveWidth = isMinimized ? 300 : size.width;
      const effectiveHeight = isMinimized ? 50 : size.height;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - effectiveWidth, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - effectiveHeight, e.clientY - dragOffset.current.y))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, size.width, size.height, isMinimized]);

  // Resize handlers
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const deltaX = e.clientX - resizeStart.current.x;
      const deltaY = e.clientY - resizeStart.current.y;

      setSize({
        width: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, resizeStart.current.width + deltaX)),
        height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, resizeStart.current.height + deltaY))
      });
    };

    const handleResizeUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeUp);
    };
  }, [isResizing]);

  // Handle expand with position adjustment
  const handleExpand = () => {
    // If expanding and the window would go off-screen, adjust position
    if (isMinimized) {
      const maxY = window.innerHeight - size.height;
      if (position.y > maxY) {
        setPosition(prev => ({ ...prev, y: Math.max(0, maxY) }));
      }
      // Also check horizontal
      const maxX = window.innerWidth - size.width;
      if (position.x > maxX) {
        setPosition(prev => ({ ...prev, x: Math.max(0, maxX) }));
      }
    }
    toggleMinimized();
  };

  // Check if there's unsaved content
  const hasUnsavedContent = () => {
    if (currentPath === 'structured') {
      return Object.values(fieldContents).some(v => v.trim());
    }
    return unstructuredContent.trim().length > 0;
  };

  // Close handler with unsaved changes check
  const handleClose = () => {
    if (hasUnsavedContent()) {
      setShowUnsavedConfirm(true);
    } else {
      // Clean close - clear any stale draft from localStorage (security: don't leave sensitive data)
      clearDraft();
      resetWindow();
      setWizardStep(null);
      setGenerationType(null);
      setProcessedContent('');
      setProcessedSOAP(null);
      setProcessingError(null);
      closeWindow();
    }
  };

  // Force close (after confirmation to discard)
  const handleForceClose = () => {
    setShowUnsavedConfirm(false);
    // User confirmed discard - clear draft from localStorage (security: don't leave sensitive data)
    clearDraft();
    resetWindow();
    setWizardStep(null);
    setGenerationType(null);
    setProcessedContent('');
    setProcessedSOAP(null);
    setProcessingError(null);
    closeWindow();
  };

  // Start AI processing with DEAN (using streaming for progress)
  const startProcessing = async (type: 'soap' | 'checkin') => {
    setGenerationType(type);
    setWizardStep('processing');
    setProcessingError(null);
    setProcessedSOAP(null);
    setProcessingProgress({ tokenCount: 0, elapsedSeconds: 0, fakeProgress: 0 });

    // Start the "fake" progress bar timer - smoothly increases over ~30 seconds
    // Goes quickly at first, then slows down as it approaches 90%
    const startTime = Date.now();
    processingTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      // Logarithmic curve: fast start, asymptotic approach to 90%
      const fakeProgress = Math.min(90, 100 * (1 - Math.exp(-elapsed / 15)));
      setProcessingProgress(prev => ({
        ...prev,
        elapsedSeconds: Math.floor(elapsed),
        fakeProgress: Math.round(fakeProgress)
      }));
    }, 100);

    try {
      const token = localStorage.getItem('auth_token');

      // Use streaming endpoint with fetch
      const response = await fetch('/api/dean/process-transcript-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          transcript: unstructuredContent,
          outputType: type === 'soap' ? 'soap' : 'custom',
          templateId: type === 'checkin' ? 'check_in' : undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to connect to DEAN');
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response stream available');
      }

      let buffer = '';
      let completionData: { success: boolean; result: unknown; warning?: string } | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (eventType && eventData) {
            try {
              const data = JSON.parse(eventData);

              if (eventType === 'progress') {
                setProcessingProgress(prev => ({
                  ...prev,
                  tokenCount: data.tokenCount
                }));
              } else if (eventType === 'complete') {
                completionData = data;
              } else if (eventType === 'error') {
                throw new Error(data.error || 'Processing failed');
              }
            } catch (parseErr) {
              // Skip malformed JSON
            }
          }
        }
      }

      // Clear the fake progress timer
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }

      // Set progress to 100% on completion
      setProcessingProgress(prev => ({ ...prev, fakeProgress: 100 }));

      if (completionData?.success && completionData.result) {
        const data = completionData;
        // If result is structured SOAP data (object), switch to structured editor
        if (typeof data.result === 'object' && type === 'soap') {
          // Switch to structured SOAP editor with pre-populated fields
          selectPath('structured');

          // Use setTimeout to ensure path change settles before template selection
          setTimeout(() => {
            selectTemplate('soap_session');

            // Another setTimeout to ensure template selection settles before populating
            setTimeout(() => {
              const result = data.result as Record<string, string>;
              // Populate each SOAP field with extracted data
              if (result.subjective) {
                updateFieldContent('subjective', result.subjective);
              }
              if (result.objective) {
                updateFieldContent('objective', result.objective);
              }
              if (result.assessment) {
                updateFieldContent('assessment', result.assessment);
              }
              if (result.plan) {
                updateFieldContent('plan', result.plan);
              }
            }, 50);
          }, 50);

          // Clear wizard state - we're now in structured editor mode
          setWizardStep(null);
          setProcessedSOAP(data.result as { subjective: string; objective: string; assessment: string; plan: string });
        } else {
          // Plain text response or non-SOAP - show in review textarea
          setProcessedContent(typeof data.result === 'object'
            ? JSON.stringify(data.result, null, 2)
            : String(data.result));
          setWizardStep('review');
        }
      } else {
        throw new Error(completionData?.warning || 'Unexpected response from DEAN');
      }
    } catch (error) {
      // Clear the fake progress timer on error
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      console.error('DEAN processing error:', error);
      setProcessingError(error instanceof Error ? error.message : 'Failed to process transcript');
      // Stay on processing screen to show error
    }
  };

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Save as raw transcript
  const saveRawTranscript = async () => {
    if (!clientId) {
      alert('No client selected. Cannot save note.');
      return;
    }

    setIsSaving(true);
    try {
      const noteData = {
        title: noteTitle || `Raw Transcript - ${new Date().toLocaleDateString()}`,
        session_date: noteDate,
        subjective: {
          content: unstructuredContent,
          client_visible: false
        },
        transcript_source: 'voice_capture',
        ai_generated: false,
        tags: ['raw-transcript']
      };

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(noteData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save note');
      }

      clearDraft();
      // Dispatch event to notify other components (like ClientSummaryView) that notes changed
      window.dispatchEvent(new CustomEvent('notes-updated', { detail: { clientId } }));
      handleForceClose();
    } catch (error) {
      console.error('Error saving raw transcript:', error);
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Save processed note (AI-generated SOAP or Check-in)
  const saveProcessedNote = async () => {
    if (!clientId) {
      alert('No client selected. Cannot save note.');
      return;
    }

    setIsSaving(true);
    try {
      // If we have structured SOAP data, save it properly to all fields
      // Otherwise fall back to single content field
      const noteData = processedSOAP ? {
        title: noteTitle || `${generationType === 'soap' ? 'SOAP Note' : 'Check-in Summary'} - ${new Date().toLocaleDateString()}`,
        session_date: noteDate,
        subjective: {
          content: processedSOAP.subjective || '',
          client_visible: false
        },
        objective: {
          content: processedSOAP.objective || '',
          client_visible: false
        },
        assessment: {
          content: processedSOAP.assessment || '',
          client_visible: false
        },
        plan: {
          content: processedSOAP.plan || '',
          client_visible: true  // Plan typically shared with client
        },
        transcript_source: 'voice_capture',
        ai_generated: true,
        tags: generationType === 'soap' ? ['ai-soap'] : ['ai-checkin']
      } : {
        // Fallback for non-structured content
        title: noteTitle || `${generationType === 'soap' ? 'SOAP Note' : 'Check-in Summary'} - ${new Date().toLocaleDateString()}`,
        session_date: noteDate,
        subjective: {
          content: processedContent,
          client_visible: false
        },
        transcript_source: 'voice_capture',
        ai_generated: true,
        tags: generationType === 'soap' ? ['ai-soap'] : ['ai-checkin']
      };

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(noteData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save note');
      }

      const result = await response.json();
      const noteId = result.note?.note_id;

      // Save raw transcript as attachment if option is enabled and we have content
      if (saveTranscript && noteId && unstructuredContent.trim()) {
        try {
          // Convert transcript text to base64
          const transcriptBase64 = btoa(unescape(encodeURIComponent(unstructuredContent)));

          await fetch(`/api/uploads/${clientId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              category: 'notes',
              content: transcriptBase64,
              original_filename: `transcript_${noteDate}.txt`,
              mime_type: 'text/plain',
              linked_to: { type: 'note', id: noteId },
              tags: ['transcript', 'voice-capture'],
              description: 'Raw session transcript from voice capture'
            })
          });
        } catch (uploadError) {
          // Log but don't fail the whole save if transcript upload fails
          console.error('Failed to save transcript attachment:', uploadError);
        }
      }

      clearDraft();
      // Dispatch event to notify other components (like ClientSummaryView) that notes changed
      window.dispatchEvent(new CustomEvent('notes-updated', { detail: { clientId } }));
      handleForceClose();
    } catch (error) {
      console.error('Error saving processed note:', error);
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Save structured note (SOAP, Quick Note)
  const saveStructuredNote = async () => {
    if (!clientId) {
      alert('No client selected. Cannot save note.');
      return;
    }

    // Title validation
    if (!noteTitle.trim()) {
      alert('Please enter a title for the note.');
      return;
    }

    setIsSaving(true);
    try {
      // Combine user tags with template type tag
      const baseTags = selectedTemplate === 'soap_session' ? ['soap'] : ['quick-note'];
      const allTags = [...new Set([...baseTags, ...noteTags])]; // Dedupe

      const noteData = {
        title: noteTitle.trim(),
        session_date: noteDate,
        subjective: {
          content: fieldContents.subjective || '',
          client_visible: fieldVisibility.subjective ?? false
        },
        objective: {
          coach_observations: fieldContents.objective || '',
          client_visible: fieldVisibility.objective ?? false
        },
        assessment: {
          content: fieldContents.assessment || '',
          client_visible: fieldVisibility.assessment ?? false
        },
        plan: {
          content: fieldContents.plan || '',
          client_visible: fieldVisibility.plan ?? true
        },
        transcript_source: null,
        ai_generated: false,
        tags: allTags
      };

      const token = localStorage.getItem('auth_token');

      // Use PUT for edit mode, POST for new notes
      const url = isEditMode && editingNoteId
        ? `/api/notes/${clientId}/${editingNoteId}`
        : `/api/notes/${clientId}`;
      const method = isEditMode && editingNoteId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(noteData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save note');
      }

      const result = await response.json();
      const noteId = isEditMode ? editingNoteId : result.note?.note_id;

      // Save raw transcript as attachment if option is enabled and we have content
      if (saveTranscript && noteId && unstructuredContent.trim()) {
        try {
          // Convert transcript text to base64
          const transcriptBase64 = btoa(unescape(encodeURIComponent(unstructuredContent)));

          await fetch(`/api/uploads/${clientId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              category: 'notes',
              content: transcriptBase64,
              original_filename: `transcript_${noteDate}.txt`,
              mime_type: 'text/plain',
              linked_to: { type: 'note', id: noteId },
              tags: ['transcript', 'voice-capture'],
              description: 'Raw session transcript from voice capture'
            })
          });
        } catch (uploadError) {
          // Log but don't fail the whole save if transcript upload fails
          console.error('Failed to save transcript attachment:', uploadError);
        }
      }

      clearDraft();
      // Dispatch event to notify other components (like ClientSummaryView) that notes changed
      window.dispatchEvent(new CustomEvent('notes-updated', { detail: { clientId } }));
      handleForceClose();
    } catch (error) {
      console.error('Error saving structured note:', error);
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Keyboard handling - Esc navigates backwards through the flow
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        if (isRecording) {
          // First priority: stop recording but keep modal open
          stopRecording();
        } else if (wizardStep === 'review') {
          // In review screen: go back to options
          setWizardStep('options');
        } else if (wizardStep === 'options') {
          // In options screen: go back to capture
          setWizardStep(null);
        } else if (wizardStep === 'processing') {
          // Processing: don't allow escape
          return;
        } else if (currentPath === 'structured' && !selectedTemplate) {
          // In template selection: go back to path selection
          selectPath(null);
        } else if (currentPath === null) {
          // In path selection: close window
          handleClose();
        } else {
          // In editor: close (with unsaved changes check)
          handleClose();
        }
      }
    };

    // Use capture phase to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, isRecording, stopRecording, currentPath, selectedTemplate, wizardStep]);

  // Convert to unstructured handler
  const handleConvertToUnstructured = () => {
    convertToUnstructured();
    setShowConvertConfirm(false);
  };

  if (!isOpen) return null;

  // Minimized state - show compact bar
  if (isMinimized) {
    // Green border when actively hearing speech, blue otherwise
    const isHearing = isRecording && interimTranscript;
    const borderColor = isHearing ? 'var(--accent-green)' : 'var(--accent-blue)';

    const minimizedBarStyle: React.CSSProperties = {
      position: 'fixed',
      left: position.x,
      top: position.y,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 18px',
      background: 'var(--bg-primary)',
      border: `2px solid ${borderColor}`,
      borderRadius: '10px',
      boxShadow: isHearing
        ? '0 6px 20px rgba(16, 185, 129, 0.3)'
        : '0 6px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 1000,
      cursor: 'grab',
      minWidth: '300px',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    };

    const controlBtnStyle: React.CSSProperties = {
      padding: '8px',
      background: 'var(--bg-tertiary)',
      border: '1px solid var(--border-secondary)',
      borderRadius: '6px',
      color: 'var(--text-primary)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
    };

    const stopBtnStyle: React.CSSProperties = {
      ...controlBtnStyle,
      background: 'rgba(229, 62, 62, 0.15)',
      borderColor: '#e53e3e',
      color: '#e53e3e',
    };

    return (
      <div style={minimizedBarStyle} onMouseDown={handleMouseDown}>
        {isRecording ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <span className="recording-dot" style={{ width: '12px', height: '12px' }} />
              <span style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 600 }}>
                {formatDuration(recordingDuration)}
              </span>
              {lockedTargetField && (
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  → {FIELD_LABELS[lockedTargetField]}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px', borderLeft: '2px solid var(--border-secondary)', flexShrink: 0 }}>
              {isPaused ? (
                <button style={{ ...controlBtnStyle, color: '#e53e3e' }} onClick={resumeRecording} title="Resume Recording">
                  <Circle size={18} fill="#e53e3e" />
                </button>
              ) : (
                <button style={controlBtnStyle} onClick={pauseRecording} title="Pause">
                  <Pause size={18} />
                </button>
              )}
              <button style={stopBtnStyle} onClick={stopRecording} title="Stop">
                <Square size={18} />
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <FileText size={18} style={{ color: 'var(--accent-blue)' }} />
            <span style={{ fontSize: '15px', fontWeight: 500 }}>Note: {noteTitle || 'Untitled'}</span>
          </div>
        )}
        <div style={{ flex: 1, minWidth: '20px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px', borderLeft: '2px solid var(--border-secondary)', flexShrink: 0 }}>
          <button style={controlBtnStyle} onClick={handleExpand} title="Expand">
            <Maximize2 size={18} />
          </button>
          <button style={{ ...controlBtnStyle, color: 'var(--text-muted)' }} onClick={handleClose} title="Close">
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Full window
  const panelStyle: React.CSSProperties = {
    left: position.x,
    top: position.y,
    width: size.width,
    height: size.height
  };

  return (
    <>
      <div
        className={`floating-notes-window ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${isRecording && interimTranscript ? 'hearing' : ''}`}
        style={panelStyle}
      >
        {/* Header */}
        <div className="notes-header" onMouseDown={handleMouseDown}>
          <div className="notes-title">
            <FileText size={18} />
            <span>
              {isEditMode
                ? 'Edit Note'
                : currentPath === null
                  ? 'New Note'
                  : currentPath === 'structured'
                    ? (selectedTemplate ? TEMPLATES.find(t => t.id === selectedTemplate)?.name : 'Select Template')
                    : wizardStep === 'options'
                      ? 'Process Capture'
                      : wizardStep === 'processing'
                        ? 'Processing...'
                        : wizardStep === 'review'
                          ? 'Review Note'
                          : 'AI-Assisted Note'}
            </span>
          </div>
          <div className="notes-controls">
            <button className="control-btn" onClick={toggleMinimized} title="Minimize">
              <Minus size={14} />
            </button>
            <button className="control-btn close-btn" onClick={handleClose} title="Close">
              <X size={14} />
            </button>
          </div>
          <div className="drag-indicator">
            <GripHorizontal size={14} />
          </div>
        </div>

        {/* Content area */}
        <div className="notes-content">
          {/* Client name bar with visibility summary */}
          {clientName && (
            <div className="client-bar">
              <span className="client-name">Client: {clientName}</span>
              {currentPath === 'structured' && selectedTemplate === 'soap_session' && (
                <div className="visibility-chips">
                  {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => (
                    <span
                      key={field}
                      className={`visibility-chip ${fieldVisibility[field] ? 'shared' : 'private'}`}
                      title={`${FIELD_LABELS[field]}: ${fieldVisibility[field] ? 'Shared with client' : 'Private'}`}
                    >
                      {fieldVisibility[field] ? <Eye size={10} /> : <EyeOff size={10} />}
                      {field.charAt(0).toUpperCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Path Selection */}
          {currentPath === null && (
            <div className="path-selection">
              <h3>Choose Your Path</h3>
              <div className="path-options">
                <button className="path-option" onClick={() => selectPath('structured')}>
                  <FileText size={24} />
                  <div className="path-text">
                    <span className="path-name">Structured Note</span>
                    <span className="path-desc">Template-based with per-field dictation</span>
                  </div>
                </button>
                <button className="path-option" onClick={() => selectPath('unstructured')}>
                  <Bot size={24} />
                  <div className="path-text">
                    <span className="path-name">AI-Assisted</span>
                    <span className="path-desc">Capture first, process with DEAN</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Template Selection (Path 1) */}
          {currentPath === 'structured' && !selectedTemplate && (
            <div className="template-selection">
              <h3>Select a Template</h3>
              <div className="template-options">
                {TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    className="template-option"
                    onClick={() => selectTemplate(template.id)}
                  >
                    <span className="template-name">{template.name}</span>
                    <span className="template-desc">{template.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Note Editor (Path 1 - Freeform) */}
          {currentPath === 'structured' && selectedTemplate === 'quick_note' && (
            <div className="quick-note-editor">
              {/* Title and date */}
              <div className="note-meta">
                <input
                  type="text"
                  className="note-title-input"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="Quick note title..."
                />
                <input
                  type="date"
                  className="note-date-input"
                  value={noteDate}
                  onChange={(e) => setNoteDate(e.target.value)}
                />
              </div>

              {/* Freeform text area */}
              <textarea
                className="quick-note-textarea"
                value={unstructuredContent}
                onChange={(e) => setUnstructuredContent(e.target.value)}
                placeholder="Type or transcribe your quick note..."
              />

              {/* Interim transcript preview - shows real-time speech */}
              {isRecording && interimTranscript && (
                <div className="interim-preview" ref={interimPreviewRef}>
                  <span className="interim-label">Hearing:</span>
                  <span className="interim-text">{interimTranscript}</span>
                </div>
              )}

              {/* Footer with recording controls and actions */}
              <div className="editor-footer structured-footer">
                <div className="footer-left">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowConvertConfirm(true)}
                    disabled={!unstructuredContent.trim()}
                  >
                    Convert to AI...
                  </button>
                  {/* Recording controls inline */}
                  {isRecording ? (
                    <>
                      {isPaused ? (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={resumeRecording} title="Resume Recording">
                          <Circle size={16} fill="#e53e3e" stroke="#e53e3e" />
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={pauseRecording} title="Pause">
                          <Pause size={16} stroke="currentColor" />
                        </button>
                      )}
                      <button className="btn btn-danger btn-sm btn-icon" onClick={stopRecording} title="Stop">
                        <Square size={16} stroke="white" />
                      </button>
                      <span className="recording-dot" />
                      <span className="recording-duration">{formatDuration(recordingDuration)}</span>
                    </>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm btn-icon mic-icon-btn"
                      onClick={startRecording}
                      disabled={!speechAvailable}
                      title={speechAvailable ? 'Start recording' : 'Voice transcription requires Chrome, Edge, or Safari'}
                    >
                      <Mic size={16} stroke="currentColor" />
                    </button>
                  )}
                </div>

                <div className="footer-right">
                  <button className="btn btn-secondary btn-sm" onClick={handleClose} disabled={isSaving}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!unstructuredContent.trim() || isSaving}
                    onClick={saveStructuredNote}
                  >
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SOAP Note Editor (Path 1 - Structured) */}
          {currentPath === 'structured' && selectedTemplate && selectedTemplate !== 'quick_note' && (
            <div className="structured-editor">
              {/* Title, date, and tags */}
              <div className="note-meta">
                <div className="note-meta-row">
                  <input
                    type="text"
                    className="note-title-input"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="Note title..."
                  />
                  <input
                    type="date"
                    className="note-date-input"
                    value={noteDate}
                    onChange={(e) => setNoteDate(e.target.value)}
                  />
                </div>
                <div className="tags-container">
                  {noteTags.map(tag => (
                    <span className="tag-chip" key={tag}>
                      {tag}
                      <button
                        type="button"
                        className="tag-chip-remove"
                        onClick={() => removeTag(tag)}
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="tag-input"
                    value={noteTagsInput}
                    onChange={(e) => setNoteTagsInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder={noteTags.length === 0 ? "Add tags (press comma or enter)" : ""}
                  />
                </div>
              </div>

              {/* SOAP Fields */}
              <div className="soap-fields">
                {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => {
                  const isExpanded = expandedField === field;
                  const isCollapsed = expandedField !== null && expandedField !== field;
                  const canToggleExpand = !isRecording; // Lock expand/collapse during recording

                  return (
                    <div
                      key={field}
                      className={`soap-field ${activeField === field ? 'active' : ''} ${lockedTargetField === field ? 'locked recording' : ''} ${isRecording && lockedTargetField !== field ? 'disabled' : ''} ${isExpanded ? 'expanded' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                    >
                      <div
                        className={`field-header ${isCollapsed ? 'clickable' : ''}`}
                        onClick={isCollapsed && canToggleExpand ? () => setExpandedField(field) : undefined}
                      >
                        <span className="field-label">{FIELD_LABELS[field]}</span>
                        {lockedTargetField === field && (
                          <span className="recording-badge">RECORDING</span>
                        )}
                        {!isCollapsed && (
                          <div className={`visibility-control ${fieldVisibility[field] ? 'shared' : ''}`}>
                            <span className="visibility-label">
                              {fieldVisibility[field] ? 'Shared' : 'Private'}
                            </span>
                            <button
                              className={`visibility-btn ${fieldVisibility[field] ? 'shared' : ''}`}
                              onClick={() => toggleFieldVisibility(field)}
                              title={fieldVisibility[field] ? 'Click to make private' : 'Click to share with client'}
                            >
                              {fieldVisibility[field] ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                          </div>
                        )}
                        <button
                          className="expand-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canToggleExpand) {
                              setExpandedField(isExpanded ? null : field);
                            }
                          }}
                          disabled={!canToggleExpand}
                          title={isExpanded ? 'Collapse' : isCollapsed ? 'Expand this section' : 'Focus on this section'}
                        >
                          {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        </button>
                      </div>
                      {!isCollapsed && (
                        <textarea
                          className="field-textarea"
                          value={fieldContents[field]}
                          onChange={(e) => updateFieldContent(field, e.target.value)}
                          onFocus={() => setActiveField(field)}
                          placeholder={`Enter ${FIELD_LABELS[field].toLowerCase()}...`}
                          disabled={isRecording && lockedTargetField !== field}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Interim transcript preview - shows real-time speech */}
              {isRecording && interimTranscript && (
                <div className="interim-preview" ref={interimPreviewRef}>
                  <span className="interim-label">Hearing:</span>
                  <span className="interim-text">{interimTranscript}</span>
                </div>
              )}

              {/* Footer with recording controls and actions */}
              <div className="editor-footer structured-footer">
                <div className="footer-left">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowConvertConfirm(true)}
                  >
                    Convert to AI...
                  </button>
                  {/* Recording controls inline with Convert button */}
                  {isRecording ? (
                    <>
                      {isPaused ? (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={resumeRecording} title="Resume Recording">
                          <Circle size={16} fill="#e53e3e" stroke="#e53e3e" />
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={pauseRecording} title="Pause">
                          <Pause size={16} stroke="currentColor" />
                        </button>
                      )}
                      <button className="btn btn-danger btn-sm btn-icon" onClick={stopRecording} title="Stop">
                        <Square size={16} stroke="white" />
                      </button>
                      <span className="recording-dot" />
                      <span className="recording-duration">{formatDuration(recordingDuration)}</span>
                    </>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm btn-icon mic-icon-btn"
                      onClick={startRecording}
                      disabled={!activeField || !speechAvailable}
                      title={!speechAvailable ? 'Voice transcription requires Chrome, Edge, or Safari' : activeField ? 'Start recording into ' + FIELD_LABELS[activeField] : 'Select a field first'}
                    >
                      <Mic size={16} stroke="currentColor" />
                    </button>
                  )}
                </div>

                <div className="footer-right">
                  {/* Transcript save option - only shows if there's unstructured content (AI-processed) */}
                  {unstructuredContent.trim() && (
                    <label className="transcript-save-option compact">
                      <input
                        type="checkbox"
                        checked={saveTranscript}
                        onChange={(e) => setSaveTranscript(e.target.checked)}
                      />
                      <span>Save transcript</span>
                    </label>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={handleClose} disabled={isSaving}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={saveStructuredNote}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Unstructured Capture (Path 2 - AI-Assisted) */}
          {currentPath === 'unstructured' && !wizardStep && (
            <div className="unstructured-editor">
              <div className="capture-header">
                <h3>Capture Content</h3>
                <p className="capture-hint">Type, paste, or transcribe your call. Process with DEAN when done.</p>
              </div>

              {/* Unstructured text area */}
              <textarea
                className="unstructured-textarea"
                value={unstructuredContent}
                onChange={(e) => setUnstructuredContent(e.target.value)}
                placeholder="Start typing, paste content, or click the mic to transcribe..."
              />

              {/* Interim transcript preview - shows real-time speech */}
              {isRecording && interimTranscript && (
                <div className="interim-preview" ref={interimPreviewRef}>
                  <span className="interim-label">Hearing:</span>
                  <span className="interim-text">{interimTranscript}</span>
                </div>
              )}

              {/* Footer with recording controls and actions */}
              <div className="editor-footer structured-footer">
                <div className="footer-left">
                  {unstructuredContent && (
                    <span className="char-count">{unstructuredContent.length} chars</span>
                  )}
                  {/* Recording controls inline */}
                  {isRecording ? (
                    <>
                      {isPaused ? (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={resumeRecording} title="Resume Recording">
                          <Circle size={16} fill="#e53e3e" stroke="#e53e3e" />
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={pauseRecording} title="Pause">
                          <Pause size={16} stroke="currentColor" />
                        </button>
                      )}
                      <button className="btn btn-danger btn-sm btn-icon" onClick={stopRecording} title="Stop">
                        <Square size={16} stroke="white" />
                      </button>
                      <span className="recording-dot" />
                      <span className="recording-duration">{formatDuration(recordingDuration)}</span>
                    </>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm btn-icon mic-icon-btn"
                      onClick={startRecording}
                      disabled={!speechAvailable}
                      title={speechAvailable ? 'Start recording' : 'Voice transcription requires Chrome, Edge, or Safari'}
                    >
                      <Mic size={16} stroke="currentColor" />
                    </button>
                  )}
                </div>

                <div className="footer-right">
                  <button className="btn btn-secondary btn-sm" onClick={handleClose}>Cancel</button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!unstructuredContent.trim()}
                    onClick={() => setWizardStep('options')}
                  >
                    Done Capturing
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Post-Processing Wizard - Options Screen */}
          {currentPath === 'unstructured' && wizardStep === 'options' && (
            <div className="wizard-screen">
              <div className="wizard-header">
                <h3>What would you like to do?</h3>
                <p className="wizard-subtitle">
                  {unstructuredContent.length.toLocaleString()} characters captured
                  {recordingDuration > 0 && ` • ${formatDuration(recordingDuration)} recording`}
                </p>
              </div>

              <div className="wizard-options">
                <button className="wizard-option" onClick={saveRawTranscript} disabled={isSaving}>
                  <div className="option-icon">
                    <FileText size={24} />
                  </div>
                  <div className="option-text">
                    <span className="option-title">{isSaving ? 'Saving...' : 'Save as Raw Transcript'}</span>
                    <span className="option-desc">Keep the unprocessed text as-is</span>
                  </div>
                </button>

                <button className="wizard-option" onClick={() => startProcessing('soap')} disabled={isSaving}>
                  <div className="option-icon accent">
                    <Bot size={24} />
                  </div>
                  <div className="option-text">
                    <span className="option-title">Generate SOAP Note</span>
                    <span className="option-desc">AI extracts structured clinical note</span>
                  </div>
                </button>

                <button className="wizard-option" onClick={() => startProcessing('checkin')} disabled={isSaving}>
                  <div className="option-icon accent">
                    <Bot size={24} />
                  </div>
                  <div className="option-text">
                    <span className="option-title">Generate Check-in Summary</span>
                    <span className="option-desc">AI creates quick summary format</span>
                  </div>
                </button>

                <button className="wizard-option" onClick={() => setWizardStep(null)} disabled={isSaving}>
                  <div className="option-icon">
                    <ChevronDown size={24} />
                  </div>
                  <div className="option-text">
                    <span className="option-title">Continue Editing</span>
                    <span className="option-desc">Go back and add more content</span>
                  </div>
                </button>
              </div>

              <div className="wizard-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setWizardStep(null)}>
                  ← Back
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => {
                  if (confirm('Discard this capture? This cannot be undone.')) {
                    clearDraft(); // Security: don't leave sensitive data in localStorage
                    setWizardStep(null);
                    setUnstructuredContent('');
                  }
                }}>
                  Discard
                </button>
              </div>
            </div>
          )}

          {/* Post-Processing Wizard - Processing Screen */}
          {currentPath === 'unstructured' && wizardStep === 'processing' && (
            <div className="wizard-screen processing">
              {processingError ? (
                <>
                  <div className="processing-error">
                    <AlertCircle size={48} />
                  </div>
                  <h3>Processing Failed</h3>
                  <p className="processing-hint error">{processingError}</p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setProcessingError(null);
                        startProcessing(generationType as 'soap' | 'checkin');
                      }}
                    >
                      Try Again
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setProcessingError(null);
                        setWizardStep('options');
                      }}
                    >
                      Back to Options
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="processing-spinner">
                    <Bot size={48} className="spin" />
                  </div>
                  <h3>Processing with DEAN...</h3>

                  {/* Progress bar container */}
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${processingProgress.fakeProgress}%` }}
                      />
                    </div>
                    <div className="progress-stats">
                      <span className="token-count">
                        {processingProgress.tokenCount > 0 ? `${processingProgress.tokenCount} tokens` : 'Starting...'}
                      </span>
                      <span className="elapsed-time">
                        {processingProgress.elapsedSeconds}s
                      </span>
                    </div>
                  </div>

                  <p className="processing-hint">Analyzing transcript and generating structured note</p>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: '16px' }}
                    onClick={() => {
                      // Clear timer on cancel
                      if (processingTimerRef.current) {
                        clearInterval(processingTimerRef.current);
                        processingTimerRef.current = null;
                      }
                      setWizardStep('options');
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}

          {/* Post-Processing Wizard - Review Screen */}
          {currentPath === 'unstructured' && wizardStep === 'review' && (
            <div className="wizard-screen review-screen">
              <div className="wizard-header">
                <h3>
                  Review {generationType === 'soap' ? 'SOAP Note' : 'Check-in Summary'}
                </h3>
                <p className="wizard-subtitle">
                  Review the AI-generated note below. Edit if needed, then save.
                </p>
              </div>

              <div className="review-content">
                <textarea
                  className="review-textarea"
                  value={processedContent}
                  onChange={(e) => setProcessedContent(e.target.value)}
                  placeholder="Note content..."
                />
              </div>

              {/* Option to save raw transcript */}
              {unstructuredContent.trim() && (
                <label className="transcript-save-option">
                  <input
                    type="checkbox"
                    checked={saveTranscript}
                    onChange={(e) => setSaveTranscript(e.target.checked)}
                  />
                  <span>Save raw transcript as attachment</span>
                </label>
              )}

              <div className="wizard-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setWizardStep('options')} disabled={isSaving}>
                  ← Back
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={saveProcessedNote}
                  disabled={!processedContent.trim() || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Resize handle */}
        <div
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
        />
      </div>

      {/* Convert confirmation modal */}
      {showConvertConfirm && (
        <div className="convert-modal-overlay">
          <div className="convert-modal">
            <AlertCircle size={32} className="warning-icon" />
            <h3>Convert to AI-Assisted?</h3>
            <p>This will combine your structured fields into unstructured text for AI processing. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowConvertConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConvertToUnstructured}>
                Convert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes confirmation modal */}
      {showUnsavedConfirm && (
        <div className="convert-modal-overlay">
          <div className="convert-modal caution-modal">
            <AlertCircle size={32} className="caution-icon" />
            <h3>Discard Unsaved Changes?</h3>
            <p>You have unsaved content in this note. Are you sure you want to close? Your changes will be lost.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowUnsavedConfirm(false)}>
                Keep Editing
              </button>
              <button className="btn btn-danger" onClick={handleForceClose}>
                Discard & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery prompt - shown when there's a saved draft from a previous session */}
      {showRecoveryPrompt && recoveryData && (
        <div className="convert-modal-overlay">
          <div className="convert-modal recovery-modal">
            <FileText size={32} className="recovery-icon" />
            <h3>Recover Unsaved Note?</h3>
            <p>
              You have an unsaved note from a previous session
              {recoveryData.clientName && ` for ${recoveryData.clientName}`}.
            </p>
            <p className="recovery-meta">
              {recoveryData.currentPath === 'structured'
                ? `${recoveryData.selectedTemplate?.toUpperCase() || 'Structured'} Note`
                : 'AI-Assisted Note'
              }
              {' • '}
              Saved {new Date(recoveryData.savedAt).toLocaleString()}
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={dismissRecovery}>
                Discard Draft
              </button>
              <button className="btn btn-primary" onClick={recoverDraft}>
                Recover Note
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .floating-notes-window {
          position: fixed;
          background: var(--bg-primary);
          border: 2px solid var(--accent-blue);
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.15), 0 4px 16px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
          z-index: 1000;
          overflow: hidden;
        }

        .floating-notes-window.hearing {
          border-color: var(--accent-green);
          box-shadow: 0 8px 32px rgba(16, 185, 129, 0.2), 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .floating-notes-window.minimized {
          width: auto !important;
          height: auto !important;
          min-width: 320px;
          max-width: 500px;
          border-radius: 8px;
          flex-direction: row !important;
        }

        .floating-notes-window.minimized > .notes-header.minimized-header {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          flex-wrap: nowrap !important;
          justify-content: flex-start !important;
          width: 100%;
          gap: 8px;
          padding: 10px 14px;
          background: var(--bg-secondary);
          border-radius: 8px;
          border-bottom: none;
        }

        .floating-notes-window.dragging {
          cursor: grabbing;
          user-select: none;
        }

        .floating-notes-window.resizing {
          user-select: none;
        }

        .notes-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          cursor: grab;
          position: relative;
          flex-shrink: 0;
          border-bottom: 1px solid var(--accent-blue);
          background: rgba(59, 130, 246, 0.05);
        }

        /* minimized-header styles are in the .floating-notes-window.minimized > .notes-header rule above */

        .floating-notes-window.dragging .notes-header {
          cursor: grabbing;
        }

        .notes-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .notes-title svg {
          color: var(--accent-blue);
        }

        .notes-controls {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .control-btn {
          padding: 6px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .control-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .control-btn.close-btn:hover {
          background: var(--accent-red, #e53e3e);
          color: white;
        }

        .drag-indicator {
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          color: var(--text-muted);
          opacity: 0.5;
        }

        .client-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: var(--text-secondary);
          padding: 12px 16px 0 16px;
          flex-shrink: 0;
          border: none;
          box-shadow: none;
          background: transparent;
        }

        .client-name {
          flex-shrink: 0;
        }

        .visibility-chips {
          display: flex;
          gap: 4px;
          align-items: center;
        }

        .visibility-chip {
          display: flex;
          align-items: center;
          gap: 2px;
          font-size: 10px;
          padding: 2px 5px;
          border-radius: 3px;
          border: 1px solid var(--border-secondary);
          color: var(--text-muted);
        }

        .visibility-chip.shared {
          color: var(--accent-green, #10b981);
          border-color: var(--accent-green, #10b981);
          background: rgba(16, 185, 129, 0.1);
        }

        .visibility-chip.private {
          color: var(--text-muted);
          border-color: var(--border-secondary);
        }

        .notes-content {
          flex: 1;
          overflow: hidden;
          padding: 0;
          display: flex;
          flex-direction: column;
          min-height: 0;
          border: none;
          box-shadow: none;
          background: var(--bg-primary);
        }

        /* Path Selection */
        .path-selection {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
        }

        .path-selection h3 {
          margin: 0 0 4px 0;
          color: var(--text-primary);
          font-size: 15px;
        }

        .path-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .path-option {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          text-align: left;
          color: var(--text-primary);
        }

        .path-option:hover {
          border-color: var(--accent-blue);
          background: var(--bg-tertiary);
        }

        .path-option svg {
          color: var(--accent-blue);
          flex-shrink: 0;
        }

        .path-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .path-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .path-desc {
          font-size: 12px;
          color: var(--text-muted);
        }

        /* Template Selection */
        .template-selection {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
        }

        .template-selection h3 {
          margin: 0 0 4px 0;
          color: var(--text-primary);
          font-size: 15px;
        }

        .template-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .template-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          padding: 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          text-align: left;
          color: var(--text-primary);
        }

        .template-option:hover {
          border-color: var(--accent-blue);
          background: var(--bg-tertiary);
        }

        .template-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .template-desc {
          font-size: 13px;
          color: var(--text-muted);
        }

        /* Structured editor - follows Modal pattern for fixed footer */
        .structured-editor {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow: hidden;
          border: none;
          box-shadow: none;
          padding: 16px 16px 0 16px;
        }

        /* Recording bar */
        .recording-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          background: var(--bg-secondary);
          border-radius: 8px;
          margin-bottom: 12px;
          flex-shrink: 0;
        }

        .recording-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-primary);
          font-size: 14px;
          flex-shrink: 0;
          white-space: nowrap;
        }

        .recording-dot {
          width: 10px;
          height: 10px;
          background: #e53e3e;
          border-radius: 50%;
          animation: pulse 1s infinite;
          flex-shrink: 0;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .duration {
          font-family: monospace;
          color: var(--text-muted);
        }

        .recording-hint {
          font-size: 13px;
          color: var(--text-muted);
        }

        /* Interim transcript preview - real-time feedback */
        /* Fixed height for ~6-8 lines, then scrolls */
        .interim-preview {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 10px 12px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px dashed var(--accent-green);
          border-radius: 6px;
          font-size: 13px;
          line-height: 1.6;
          min-height: 80px;
          max-height: 160px;
          overflow-y: auto;
          animation: pulse 1.5s ease-in-out infinite;
          flex-shrink: 0;
          margin-bottom: 12px;
        }

        .interim-preview::-webkit-scrollbar {
          width: 6px;
        }

        .interim-preview::-webkit-scrollbar-track {
          background: transparent;
        }

        .interim-preview::-webkit-scrollbar-thumb {
          background: var(--accent-green);
          border-radius: 3px;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .interim-label {
          color: var(--accent-green);
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
          padding-top: 1px;
        }

        .interim-text {
          color: var(--text-secondary);
          font-style: italic;
          flex: 1;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .recording-controls {
          display: flex;
          gap: 8px;
        }

        .rec-btn {
          padding: 8px;
          background: var(--bg-tertiary);
          border: none;
          border-radius: 6px;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.15s;
        }

        .rec-btn:hover {
          background: var(--bg-primary);
        }

        .rec-btn.record {
          background: rgba(229, 62, 62, 0.15);
          color: #e53e3e;
        }

        .rec-btn.record:hover {
          background: rgba(229, 62, 62, 0.25);
        }

        .rec-btn.stop {
          background: #e53e3e;
          color: white;
        }

        .rec-btn.stop:hover {
          background: #c53030;
        }

        .mic-btn {
          padding: 10px;
          background: var(--accent-blue);
          border: none;
          border-radius: 8px;
          color: white;
          cursor: pointer;
          transition: all 0.15s;
        }

        .mic-btn:hover:not(:disabled) {
          opacity: 0.9;
        }

        .mic-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Note meta */
        .note-meta {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
          flex-shrink: 0;
          border: none;
          box-shadow: none;
        }

        .note-meta-row {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .note-title-input {
          flex: 1;
          min-width: 0;
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
        }

        .note-title-input:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .note-date-input {
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          flex-shrink: 0;
          width: auto;
        }

        .note-date-input:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .note-date-input::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }

        .tags-container {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          width: 100%;
          padding: 6px 10px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          min-height: 32px;
          cursor: text;
          box-sizing: border-box;
        }

        .tags-container:focus-within {
          border-color: var(--accent-blue);
        }

        .tag-chip {
          display: inline-flex;
          align-items: center;
          gap: 0;
          padding: 2px 8px;
          background: var(--accent-blue);
          color: white;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          transition: gap 0.15s;
        }

        .tag-chip:hover {
          gap: 4px;
        }

        .tag-chip-remove {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 0;
          height: 14px;
          padding: 0;
          margin: 0;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 12px;
          line-height: 1;
          cursor: pointer;
          overflow: hidden;
          opacity: 0;
          transition: width 0.15s, opacity 0.15s, background 0.15s;
        }

        .tag-chip:hover .tag-chip-remove {
          width: 14px;
          opacity: 1;
        }

        .tag-chip-remove:hover {
          background: rgba(255, 255, 255, 0.4);
        }

        .tag-input {
          flex: 1;
          min-width: 100px;
          padding: 2px 0;
          background: transparent;
          border: none;
          color: var(--text-primary);
          font-size: 12px;
          outline: none;
        }

        .tag-input::placeholder {
          color: var(--text-muted);
        }

        /* SOAP Fields - only expand when a field is expanded */
        .soap-fields {
          display: flex;
          flex-direction: column;
          gap: 12px;
          overflow-y: auto;
          min-height: 0;
          flex: 1;
        }

        /* When a field is expanded, soap-fields already has flex: 1 */
        .soap-fields:has(.soap-field.expanded) {
          /* flex: 1 is now baseline */
        }

        .soap-field {
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s ease;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }

        .soap-field.active {
          border-color: var(--accent-blue);
        }

        .soap-field.locked.recording {
          border-color: #e53e3e;
          border-width: 2px;
        }

        .soap-field.disabled {
          opacity: 0.5;
        }

        /* Expanded field takes all available space */
        .soap-field.expanded {
          flex: 1;
          min-height: 0;
        }

        .soap-field.expanded .field-textarea {
          flex: 1;
          min-height: 0;
          height: 100%;
        }

        /* Collapsed fields show only header */
        .soap-field.collapsed {
          flex-shrink: 0;
        }

        .soap-field.collapsed .field-header {
          border-bottom: none;
        }

        .soap-field.collapsed .field-header.clickable {
          cursor: pointer;
        }

        .soap-field.collapsed .field-header.clickable:hover {
          background: var(--bg-tertiary);
        }

        .field-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-primary);
          flex-shrink: 0;
        }

        .field-label {
          font-weight: 600;
          font-size: 13px;
          color: var(--text-primary);
        }

        .recording-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          background: #e53e3e;
          color: white;
          border-radius: 4px;
          animation: pulse 1s infinite;
        }

        .visibility-control {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: auto;
        }

        .visibility-label {
          font-size: 11px;
          color: var(--text-muted);
        }

        .visibility-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: 4px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .visibility-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        /* Green styling when section is shared with client */
        .visibility-control.shared .visibility-label {
          color: var(--accent-green, #10b981);
          font-weight: 500;
        }

        .visibility-btn.shared {
          color: var(--accent-green, #10b981);
          border-color: var(--accent-green, #10b981);
        }

        .visibility-btn.shared:hover {
          background: rgba(16, 185, 129, 0.1);
        }

        .expand-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: 4px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        /* When collapsed, visibility-control isn't shown, so expand-btn needs to push right */
        .soap-field.collapsed .expand-btn {
          margin-left: auto;
        }

        .expand-btn:hover:not(:disabled) {
          background: var(--bg-tertiary);
          color: var(--accent-blue);
          border-color: var(--accent-blue);
        }

        .expand-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .field-textarea {
          width: 100%;
          min-height: 72px;
          padding: 10px 12px;
          border: none;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.5;
          resize: none;
        }

        .field-textarea:focus {
          outline: none;
        }

        .field-textarea:disabled {
          background: var(--bg-secondary);
          cursor: not-allowed;
        }

        /* Quick Note editor - follows Modal pattern for fixed footer */
        .quick-note-editor {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow: hidden;
          padding: 16px 16px 0 16px;
        }

        .quick-note-textarea {
          flex: 1;
          min-height: 150px;
          padding: 12px;
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 14px;
          resize: none;
          margin-bottom: 12px;
        }

        .quick-note-textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        /* Unstructured editor - follows Modal pattern for fixed footer */
        .unstructured-editor {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow: hidden;
          padding: 16px 16px 0 16px;
        }

        .capture-header {
          flex-shrink: 0;
          margin-bottom: 12px;
        }

        .capture-header h3 {
          margin: 0 0 4px 0;
          color: var(--text-primary);
        }

        .capture-hint {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }

        .unstructured-textarea {
          flex: 1;
          min-height: 100px;
          padding: 12px;
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 14px;
          resize: none;
          margin-bottom: 12px;
        }

        .unstructured-textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        /* Post-Processing Wizard */
        .wizard-screen {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          padding: 16px 16px 0 16px;
        }

        .wizard-screen.processing {
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .wizard-header {
          margin-bottom: 20px;
        }

        .wizard-header h3 {
          margin: 0 0 8px 0;
          color: var(--text-primary);
        }

        .wizard-subtitle {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }

        .wizard-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
          overflow-y: auto;
        }

        .wizard-option {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s;
          text-align: left;
          color: var(--text-primary);
        }

        .wizard-option:hover {
          border-color: var(--accent-blue);
          background: var(--bg-tertiary);
        }

        .option-icon {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          background: var(--bg-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          flex-shrink: 0;
        }

        .option-icon.accent {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }

        .option-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .option-title {
          font-weight: 600;
          font-size: 15px;
          color: var(--text-primary);
        }

        .option-desc {
          font-size: 13px;
          color: var(--text-muted);
        }

        .wizard-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          margin: auto -16px 0 -16px;
          flex-shrink: 0;
          border-top: 1px solid var(--accent-blue);
          background: rgba(59, 130, 246, 0.05);
        }

        .processing-spinner {
          margin-bottom: 20px;
          color: var(--accent-blue);
        }

        .processing-spinner .spin {
          animation: spin 2s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .processing-hint {
          color: var(--text-muted);
          font-size: 14px;
        }

        .processing-hint.error {
          color: var(--danger);
        }

        .processing-error {
          color: var(--danger);
          margin-bottom: 16px;
        }

        /* Progress bar styles */
        .progress-container {
          width: 100%;
          max-width: 280px;
          margin: 20px 0;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--accent-blue), #60a5fa);
          border-radius: 4px;
          transition: width 0.2s ease-out;
        }

        .progress-stats {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: var(--text-muted);
        }

        .token-count {
          font-family: monospace;
        }

        .elapsed-time {
          font-family: monospace;
        }

        .review-placeholder {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          text-align: center;
        }

        .review-screen {
          display: flex;
          flex-direction: column;
        }

        .review-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 0 16px 16px 16px;
          min-height: 0;
        }

        .review-textarea {
          flex: 1;
          width: 100%;
          padding: 12px;
          font-family: inherit;
          font-size: 14px;
          line-height: 1.5;
          color: var(--text-primary);
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          resize: none;
          min-height: 200px;
        }

        .review-textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .transcript-save-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          font-size: 13px;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .transcript-save-option input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: var(--accent-blue);
        }

        .transcript-save-option:hover {
          color: var(--text-primary);
        }

        .transcript-save-option.compact {
          padding: 0;
          margin-right: 12px;
          font-size: 12px;
        }

        .ai-note {
          color: var(--text-muted);
          font-style: italic;
          font-size: 12px;
        }

        .text-muted {
          color: var(--text-muted);
          font-size: 13px;
        }

        /* Editor footer - fixed at bottom (Modal pattern) */
        /* Negative margins extend past parent's horizontal padding for full width */
        .editor-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          margin: auto -16px 0 -16px;
          flex-shrink: 0;
          border-top: 1px solid var(--accent-blue);
          background: rgba(59, 130, 246, 0.05);
        }

        .structured-footer {
          display: flex;
          justify-content: space-between;
          gap: 12px;
        }

        .footer-left {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: flex-start;
        }

        .footer-right {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: flex-end;
        }

        .recording-duration {
          font-family: monospace;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .btn-sm {
          padding: 8px 14px;
          font-size: 13px;
          height: 36px;
          box-sizing: border-box;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-icon svg {
          flex-shrink: 0;
        }

        .btn-secondary.btn-icon {
          color: var(--text-primary);
        }

        .btn-secondary.btn-icon:hover {
          color: var(--accent-blue);
          border-color: var(--accent-blue);
        }

        .mic-icon-btn {
          color: var(--accent-blue);
          border-color: var(--accent-blue);
        }

        .mic-icon-btn:disabled {
          color: var(--text-muted);
          border-color: var(--border-primary);
        }

        .char-count {
          font-size: 12px;
          color: var(--text-muted);
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary {
          background: var(--accent-blue);
          border: none;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: transparent;
          border: 1px solid var(--border-primary);
          color: var(--text-secondary);
        }

        .btn-secondary:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        /* Resize handle - diagonal grip lines in bottom-right triangle */
        .resize-handle {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 24px;
          height: 24px;
          cursor: se-resize;
          z-index: 10;
          /* Visual lines using repeating gradient - subdued blue */
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 2px,
            rgba(59, 130, 246, 0.4) 2px,
            rgba(59, 130, 246, 0.4) 4px
          );
          margin: 4px;
          transition: background 0.15s;
          /* Clip to triangle shape - bottom right corner */
          clip-path: polygon(100% 0%, 100% 100%, 0% 100%);
        }

        .resize-handle:hover {
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 2px,
            var(--accent-blue) 2px,
            var(--accent-blue) 4px
          );
        }

        /* Convert modal */
        .convert-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
        }

        .convert-modal {
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          text-align: center;
        }

        .convert-modal .warning-icon {
          color: var(--accent-orange);
          margin-bottom: 12px;
        }

        .convert-modal.caution-modal {
          border: 2px solid var(--accent-orange, #f59e0b);
        }

        .convert-modal .caution-icon {
          color: var(--accent-orange, #f59e0b);
          margin-bottom: 12px;
        }

        .convert-modal.recovery-modal {
          border: 2px solid var(--accent-blue);
        }

        .convert-modal .recovery-icon {
          color: var(--accent-blue);
          margin-bottom: 12px;
        }

        .recovery-meta {
          font-size: 13px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .convert-modal h3 {
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .convert-modal p {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 20px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .btn-danger {
          background: #e53e3e;
          border: none;
          color: white;
        }

        .btn-danger:hover {
          background: #c53030;
        }

        /* Minimized state extras */
        .minimized-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-primary);
          white-space: nowrap;
        }

        .minimized-spacer {
          flex: 1;
          min-width: 12px;
        }

        .minimized-recording-controls {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
          padding-left: 8px;
          border-left: 1px solid var(--border-secondary);
          flex-shrink: 0;
        }

        .minimized-recording-controls .stop-btn {
          color: #e53e3e;
        }

        .minimized-recording-controls .stop-btn:hover {
          background: rgba(229, 62, 62, 0.1);
        }

        .minimized-window-controls {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 2px;
          padding-left: 8px;
          border-left: 1px solid var(--border-secondary);
          flex-shrink: 0;
        }

        .recording-target {
          font-size: 12px;
          color: var(--text-muted);
          margin-left: 4px;
        }

      `}</style>
    </>
  );
};

export default FloatingNotesWindow;
